import React, { useState } from 'react';

import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ScreenRotationAltOutlinedIcon from '@mui/icons-material/ScreenRotationAltOutlined';
import {
  Alert,
  Box,
  Button,
  Collapse,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import { BRAND_COLORS } from '~/components/brand';

import { LENS_CARD_TYPE_OPTIONS } from '~/types/leLens';
import type { LensCard, LensCardType, LensTextScale, LensValueLabelsConfig } from '~/types/leLens';

import ChartTypeGlyph from '../../ChartTypeGlyph';
import { CARD_TYPE_GUIDANCE } from '../../cardTypeGuidance';
import { chartFitIssue } from '../../lensVisualAudit';

import { cardShowsSeriesGrouping } from './useStyleTabState';

// Keep the existing semantic ranking, then omit alternatives that fail the
// same loaded-result checks used by the board's visual audit.

const TIME_FIELD = /(date|day|week|month|quarter|year|hour|period|time)/i;
const VALUE_FIELD = /(amount|average|avg|count|margin|number|orders?|percent|rate|revenue|sales|total|value)/i;

/** Short, tile-sized names, distinct from LENS_CARD_TYPE_OPTIONS' fuller labels used in selects elsewhere. */
const TILE_LABEL: Record<LensCardType, string> = {
  metric: 'Metric',
  line: 'Line',
  bar: 'Bar',
  drivers: 'Drivers',
  stacked: 'Stacked bar',
  area: 'Area',
  scatter: 'Scatter',
  pie: 'Pie',
  combo: 'Combo',
  heatmap: 'Heatmap',
  boxplot: 'Box plot',
  table: 'Table',
  insight: 'Insight',
};

/** A visual type is chosen from the card's title, description and returned
 *  columns: the same three-tile "recommended" heuristic the legacy dialog
 *  used, so opening a card here suggests exactly what it suggested there. */
export function chartTypeRecommendations({
  currentType,
  title,
  description,
  columns,
  grains,
  seriesNames,
  selectedSeries,
}: {
  currentType: LensCardType;
  title: string;
  description: string;
  columns: Array<string>;
  grains: Array<string>;
  seriesNames: Array<string>;
  selectedSeries: string;
}): Array<LensCardType> {
  const detail = `${title} ${description} ${columns.join(' ')}`.toLowerCase();
  const hasTime = [...columns, ...grains].some((field) => TIME_FIELD.test(field));
  const hasMeasure = columns.some((field) => VALUE_FIELD.test(field));
  const hasSeries = Boolean(selectedSeries) || seriesNames.length > 1 || columns.length >= 3;
  const recommended: Array<LensCardType> = [];

  if (/drivers?|contribution|change by/.test(detail)) {
    recommended.push('drivers');
  } else if (/distribution|spread|median|quartile/.test(detail)) {
    recommended.push('boxplot');
  } else if (/relationship|correlation|versus| vs\.? /.test(detail)) {
    recommended.push('scatter');
  } else if (/share|proportion|composition|mix|percentage of/.test(detail)) {
    recommended.push(hasSeries ? 'stacked' : 'pie');
  } else if (hasTime) {
    recommended.push(hasSeries ? 'stacked' : 'line');
  } else if (columns.length <= 1 || !hasMeasure) {
    recommended.push('metric');
  } else {
    recommended.push('bar');
  }

  if (hasTime && !recommended.includes('line')) {
    recommended.push('line');
  }
  if (hasSeries && !recommended.includes('stacked')) {
    recommended.push('stacked');
  }
  if (!recommended.includes('metric')) {
    recommended.push('metric');
  }
  if (!recommended.includes(currentType)) {
    recommended.unshift(currentType);
  }

  return Array.from(new Set(recommended)).slice(0, 3);
}

function ChartTypeChoice({
  active,
  disabled,
  onChoose,
  tokens,
  type,
}: {
  active: boolean;
  disabled: boolean;
  onChoose: (type: LensCardType) => void;
  tokens: ReturnType<typeof lensTokens>;
  type: LensCardType;
}) {
  return (
    <Button
      aria-checked={active}
      disabled={disabled}
      onClick={() => onChoose(type)}
      role="radio"
      sx={{
        alignItems: 'center',
        border: active ? '1.5px solid' : 1,
        borderColor: active ? tokens.accent : 'divider',
        borderRadius: '8px',
        bgcolor: active ? alpha(tokens.accent, 0.08) : 'background.paper',
        color: active ? tokens.accent : 'text.primary',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.375,
        justifyContent: 'center',
        minHeight: 58,
        minWidth: 0,
        px: 0.5,
        py: 0.75,
        textAlign: 'center',
        textTransform: 'none',
        transition: 'border-color 140ms ease, background-color 140ms ease',
        width: '100%',
        '&:hover': { bgcolor: active ? alpha(tokens.accent, 0.12) : 'action.hover', borderColor: tokens.accent },
        '&.Mui-disabled': { borderColor: 'divider' },
      }}
      variant="outlined"
    >
      <Box
        aria-hidden
        sx={{
          alignItems: 'center',
          display: 'flex',
          height: 22,
          justifyContent: 'center',
          '& svg': { display: 'block', height: 22, width: 'auto' },
        }}
      >
        <ChartTypeGlyph type={type} />
      </Box>
      <Typography component="span" sx={{ fontSize: 11, fontWeight: active ? 600 : 500, lineHeight: 1.25 }}>
        {TILE_LABEL[type]}
      </Typography>
    </Button>
  );
}

/** A static, illustrative trend, independent of the card's own data: the
 *  panel does not have loaded rows to draw a real preview from, and the
 *  point here is to show what the switch turns on, not this card's numbers. */
function SparklinePreview({ tokens }: { tokens: ReturnType<typeof lensTokens> }) {
  return (
    <Box
      aria-hidden
      component="svg"
      height={18}
      sx={{ display: 'block', flexShrink: 0 }}
      viewBox="0 0 60 18"
      width={60}
    >
      <path
        d="M2 14 L14 10 L26 12 L38 5 L50 8 L58 3"
        fill="none"
        stroke={tokens.accent}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
      />
      <circle cx={58} cy={3} fill={BRAND_COLORS.coralGreen} r={2.4} />
    </Box>
  );
}

/** The draft this tab reports on every choice: just the one field it owns. */
export type ChartTabDraft = { type: LensCardType };

export type ChartTabProps = {
  /** The card as it stands before this tab's own edit; title/description
   *  (and, for a legacy own-query card, its columns) feed the recommendation
   *  heuristic the same way they did inside the dialog. */
  card: LensCard;
  /** Declared columns (a dataset's, or a legacy card's returned columns) the
   *  recommendation heuristic reads for a time or value field. */
  columnNames: Array<string>;
  /** Coarser time buckets offered beside the columns, for the same check. */
  grainNames?: Array<string>;
  /** Series from the last render, so a "split" recommendation can tell a
   *  chosen split from one merely available. */
  seriesNames?: Array<string>;
  /** The view's current split field, if any. */
  viewSeries?: string;
  /** The field actually on the horizontal axis; dataset availability alone does not make a trend suitable. */
  viewX?: string;
  /** Loaded results, when available, keep unsuitable types out of the short suggestion list. */
  rows?: Array<Record<string, unknown>>;
  /** The draft value of the one field this tab owns. */
  draft: ChartTabDraft;
  onChange: (next: ChartTabDraft) => void;
  busy?: boolean;
  /** Metric cards only: whether the trend line beside (or, centred, under)
   *  the figure shows. Undefined reads as on, matching the config default. */
  sparkline?: boolean;
  onSparklineChange?: (value: boolean) => void;
  /** Axis charts only: print formatted values beside their marks. */
  valueLabels?: boolean;
  onValueLabelsChange?: (value: boolean) => void;
  valueLabelMode?: NonNullable<LensValueLabelsConfig['mode']>;
  onValueLabelModeChange?: (value: NonNullable<LensValueLabelsConfig['mode']>) => void;
  valueLabelSize?: LensTextScale;
  onValueLabelSizeChange?: (value: LensTextScale) => void;
  /** Series-grouping types only (bar, stacked, line, area, combo): stack
   *  series instead of grouping them side by side. */
  stacked?: boolean;
  barOrientation?: 'vertical' | 'horizontal';
  onBarOrientationChange?: (value: 'vertical' | 'horizontal') => void;
  onStackedChange?: (value: boolean) => void;
};

/**
 * The panel's Visual section: current type and two suitable alternatives,
 * with the full gallery on request. Shape-specific controls follow below.
 */
export default function ChartTab({
  busy = false,
  card,
  columnNames,
  draft,
  grainNames = [],
  onChange,
  onSparklineChange,
  onStackedChange,
  onValueLabelsChange,
  onValueLabelModeChange,
  onValueLabelSizeChange,
  onBarOrientationChange,
  barOrientation,
  seriesNames = [],
  sparkline,
  stacked,
  valueLabels,
  valueLabelMode = 'values',
  valueLabelSize = 'medium',
  viewSeries = '',
  viewX = '',
  rows,
}: ChartTabProps) {
  const [showAllTypes, setShowAllTypes] = useState(false);
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const chooseType = (next: LensCardType) => {
    onChange({ type: next });
  };

  const suggestedTypes = chartTypeRecommendations({
    currentType: draft.type,
    title: card.title,
    description: card.description ?? '',
    columns: columnNames,
    grains: grainNames,
    seriesNames,
    selectedSeries: viewSeries,
  });
  const hasTimeAxis = viewX
    ? TIME_FIELD.test(viewX)
    : [...columnNames, ...grainNames].some((field) => TIME_FIELD.test(field));
  const hasSplit = Boolean(viewSeries) || seriesNames.length > 1;
  const fitsRows = (type: LensCardType) => {
    if (!rows?.length) return true;
    if ((type === 'line' || type === 'area') && !hasTimeAxis) return false;
    if ((type === 'stacked' || type === 'combo') && !hasSplit) return false;
    if (type === 'metric' && rows.length > 1) return false;
    return !chartFitIssue({ ...card, type }, rows);
  };
  const currentFitIssue = rows?.length
    ? (chartFitIssue({ ...card, type: draft.type }, rows) ??
      ((draft.type === 'line' || draft.type === 'area') && !hasTimeAxis
        ? 'This trend needs a date or time field on its axis.'
        : undefined))
    : undefined;
  const alternativeOrder: Array<LensCardType> = [
    'table',
    'bar',
    'pie',
    'line',
    'stacked',
    'area',
    'metric',
    'drivers',
    'scatter',
    'combo',
    'heatmap',
    'boxplot',
  ];
  const recommendedTypes = [
    draft.type,
    ...suggestedTypes.filter((type) => type !== draft.type && fitsRows(type)),
    ...alternativeOrder.filter((type) => type !== draft.type && fitsRows(type)),
  ]
    .filter((type, index, all) => all.indexOf(type) === index)
    .slice(0, 3);
  const moreChartTypes = LENS_CARD_TYPE_OPTIONS.filter((option) => !recommendedTypes.includes(option.value));
  const showsBarOrientation = draft.type === 'bar' || draft.type === 'stacked';
  const nextBarOrientation = barOrientation === 'horizontal' ? 'vertical' : 'horizontal';
  const dataLabelModes: Array<{ label: string; value: NonNullable<LensValueLabelsConfig['mode']> }> =
    draft.type === 'stacked'
      ? [
          { label: 'Totals only', value: 'totals' },
          { label: 'Each segment', value: 'values' },
        ]
      : draft.type === 'bar'
        ? [
            { label: 'Every bar', value: 'values' },
            { label: 'Top 3 bars', value: 'top' },
          ]
        : [
            { label: 'Last value', value: 'last' },
            { label: 'Sampled values', value: 'values' },
          ];
  const effectiveValueLabelMode = dataLabelModes.some((option) => option.value === valueLabelMode)
    ? valueLabelMode
    : dataLabelModes[0].value;

  return (
    <Stack spacing={3}>
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 0.25 }}>Chart type</Typography>
        <Typography color="text.secondary" sx={{ display: 'block', mb: 1.5 }} variant="body2">
          {CARD_TYPE_GUIDANCE[draft.type]}
        </Typography>
        <Typography color="text.secondary" sx={{ display: 'block', fontSize: 11, fontWeight: 600, mb: 1 }}>
          {rows?.length ? 'Current and suitable alternatives' : 'Current and suggested'}
        </Typography>
        <Box
          aria-label="Recommended visual types"
          role="radiogroup"
          sx={{ alignItems: 'stretch', display: 'flex', gap: 1 }}
        >
          {recommendedTypes.map((cardType) => (
            <Box key={cardType} sx={{ flex: '1 1 0', minWidth: 0 }}>
              <ChartTypeChoice
                active={draft.type === cardType}
                disabled={busy}
                onChoose={chooseType}
                tokens={tokens}
                type={cardType}
              />
            </Box>
          ))}
        </Box>
      </Box>

      {currentFitIssue && <Alert severity="warning">{currentFitIssue}</Alert>}

      {moreChartTypes.length > 0 && (
        <Box>
          <Button
            aria-expanded={showAllTypes}
            endIcon={<ExpandMoreIcon sx={{ transform: showAllTypes ? 'rotate(180deg)' : 'none' }} />}
            onClick={() => setShowAllTypes((current) => !current)}
            size="small"
            sx={{ px: 0, textTransform: 'none' }}
          >
            {showAllTypes ? 'Hide chart types' : 'More chart types'}
          </Button>
          <Collapse in={showAllTypes} unmountOnExit>
            <Box
              aria-label="All visual types"
              role="radiogroup"
              sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', mt: 1 }}
            >
              {moreChartTypes.map((option) => (
                <ChartTypeChoice
                  active={draft.type === option.value}
                  disabled={busy}
                  key={option.value}
                  onChoose={chooseType}
                  tokens={tokens}
                  type={option.value}
                />
              ))}
            </Box>
          </Collapse>
        </Box>
      )}

      {showsBarOrientation && onBarOrientationChange && (
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ fontWeight: 600 }} variant="body2">
              Orientation
            </Typography>
            <Typography color="text.secondary" variant="caption">
              {barOrientation === 'horizontal' ? 'Horizontal bars' : 'Vertical bars'}
            </Typography>
          </Box>
          <Button
            disabled={busy}
            onClick={() => onBarOrientationChange(nextBarOrientation)}
            startIcon={<ScreenRotationAltOutlinedIcon />}
            variant="outlined"
          >
            {`Flip to ${nextBarOrientation}`}
          </Button>
        </Stack>
      )}

      {draft.type === 'metric' && onSparklineChange && (
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ fontWeight: 600 }} variant="body2">
              Sparkline
            </Typography>
            <Typography color="text.secondary" variant="caption">
              Independent of title alignment.
            </Typography>
          </Box>
          <Stack alignItems="center" direction="row" spacing={1.5}>
            <SparklinePreview tokens={tokens} />
            <Switch
              checked={sparkline ?? true}
              disabled={busy}
              onChange={(event) => onSparklineChange(event.target.checked)}
              slotProps={{ input: { 'aria-checked': sparkline ?? true, 'aria-label': 'Sparkline' } }}
            />
          </Stack>
        </Stack>
      )}

      {cardShowsSeriesGrouping(draft.type) && onValueLabelsChange && (
        <Stack spacing={1.5}>
          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ pr: 2 }}>
              <Typography sx={{ fontWeight: 600 }} variant="body2">
                Data labels
              </Typography>
            </Box>
            <Switch
              checked={Boolean(valueLabels)}
              disabled={busy}
              onChange={(event) => {
                if (event.target.checked && effectiveValueLabelMode !== valueLabelMode) {
                  onValueLabelModeChange?.(effectiveValueLabelMode);
                }
                onValueLabelsChange(event.target.checked);
              }}
              slotProps={{ input: { 'aria-checked': Boolean(valueLabels), 'aria-label': 'Data labels' } }}
            />
          </Stack>
          {valueLabels && (
            <>
              <Stack direction="row" spacing={1}>
                <FormControl fullWidth size="small">
                  <InputLabel id="le-lens-data-label-mode">Show</InputLabel>
                  <Select
                    disabled={busy}
                    label="Show"
                    labelId="le-lens-data-label-mode"
                    onChange={(event) => onValueLabelModeChange?.(event.target.value)}
                    value={effectiveValueLabelMode}
                  >
                    {dataLabelModes.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel id="le-lens-data-label-size">Size</InputLabel>
                  <Select
                    disabled={busy}
                    label="Size"
                    labelId="le-lens-data-label-size"
                    onChange={(event) => onValueLabelSizeChange?.(event.target.value)}
                    value={valueLabelSize}
                  >
                    <MenuItem value="small">Small</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="large">Large</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              <Typography color="text.secondary" variant="caption">
                Currency, percentage and decimal places are set in Appearance.
              </Typography>
            </>
          )}
        </Stack>
      )}

      {cardShowsSeriesGrouping(draft.type) && draft.type !== 'bar' && draft.type !== 'stacked' && onStackedChange && (
        <Stack>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(stacked)}
                disabled={busy}
                onChange={(event) => onStackedChange(event.target.checked)}
                size="small"
                slotProps={{ input: { 'aria-checked': Boolean(stacked) } }}
              />
            }
            label="Stacked"
            slotProps={{ typography: { variant: 'body2' } }}
          />
        </Stack>
      )}
    </Stack>
  );
}
